import { useMemo } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, Textarea } from '@solid/ui';

type QuestionImportance = 'REQUIRED' | 'PREFERRED' | 'NICE_TO_HAVE';

interface Question {
  questionId: string;
  question: string;
  importance: QuestionImportance;
  helpText?: string;
}

interface QuestionsFormProps {
  questions: Question[];
  answers: Record<string, string>;
  onAnswer: (questionId: string, answer: string) => void;
  currentPage: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  onSubmit?: () => void;
  questionsPerPage?: number;
  /** Always show back button, even on first page (for wizard-level back nav) */
  showBack?: boolean;
}

const importanceBadgeStyles: Record<QuestionImportance, string> = {
  REQUIRED: 'bg-red-500/20 text-red-400',
  PREFERRED: 'bg-yellow-500/20 text-yellow-400',
  NICE_TO_HAVE: 'bg-blue-500/20 text-blue-400',
};

const importanceLabels: Record<QuestionImportance, string> = {
  REQUIRED: 'Required',
  PREFERRED: 'Preferred',
  NICE_TO_HAVE: 'Optional',
};

export function QuestionsForm({
  questions,
  answers,
  onAnswer,
  currentPage,
  onNextPage,
  onPrevPage,
  onSubmit,
  questionsPerPage = 2,
  showBack = false,
}: QuestionsFormProps) {
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  const pageQuestions = useMemo(() => {
    const start = currentPage * questionsPerPage;
    return questions.slice(start, start + questionsPerPage);
  }, [questions, currentPage, questionsPerPage]);

  // Check whether the current page has all required questions answered
  const canProceed = useMemo(() => {
    return pageQuestions.every((q) => {
      if (q.importance !== 'REQUIRED') return true;
      const answer = answers[q.questionId];
      return answer !== undefined && answer.trim().length > 0;
    });
  }, [pageQuestions, answers]);

  return (
    <div className="space-y-6">
      {pageQuestions.map((q, index) => {
        const isOptional = q.importance === 'NICE_TO_HAVE';
        const questionNumber =
          currentPage * questionsPerPage + index + 1;

        return (
          <div
            key={q.questionId}
            className="rounded-xl border border-slate-700 bg-slate-800 p-5 sm:p-6"
          >
            {/* Question header */}
            <div className="mb-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <label
                  htmlFor={`question-${q.questionId}`}
                  className="text-base font-semibold text-white sm:text-lg leading-snug"
                >
                  {q.importance === 'REQUIRED' && (
                    <span className="text-red-400 mr-1" aria-hidden="true">
                      *
                    </span>
                  )}
                  {q.question}
                </label>
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                    importanceBadgeStyles[q.importance]
                  )}
                >
                  {importanceLabels[q.importance]}
                </span>
              </div>

              {q.helpText && (
                <p className="text-sm text-slate-400 leading-relaxed">
                  {q.helpText}
                </p>
              )}
            </div>

            {/* Answer input */}
            <Textarea
              id={`question-${q.questionId}`}
              value={answers[q.questionId] || ''}
              onChange={(e) => onAnswer(q.questionId, e.target.value)}
              placeholder={
                isOptional
                  ? 'Optional - skip if not applicable'
                  : 'Type your answer...'
              }
              textareaClassName="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-accent-500 focus:ring-accent-500/20"
              rows={3}
              aria-required={!isOptional}
              aria-label={`Question ${questionNumber}: ${q.question}`}
            />

            {isOptional && (
              <p className="mt-2 text-xs text-slate-500">
                This question is optional and can be skipped.
              </p>
            )}
          </div>
        );
      })}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onPrevPage}
          disabled={isFirstPage && !showBack}
          className={cn(
            'text-slate-300 hover:text-white hover:bg-slate-800',
            isFirstPage && !showBack && 'invisible'
          )}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <div className="text-xs text-slate-500">
          {currentPage + 1} of {totalPages}
        </div>

        <Button
          type="button"
          variant="primary"
          onClick={isLastPage && onSubmit ? onSubmit : onNextPage}
          disabled={!canProceed}
          className="bg-accent-600 hover:bg-accent-700 text-white"
        >
          {isLastPage ? 'Submit' : 'Next'}
          {!isLastPage && <ArrowRight className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
